<template>
  <VDialog
    :value="$store.state.ui.registerModal"
    persistent
    width="500"
    @input="$store.commit('ui/setRegisterModal', { value: $event })"
  >
    <VCard
      class="mx-auto"
      max-width="500"
    >
      <VCardTitle class="title font-weight-regular justify-space-between">
        <span>Регистрация</span>
        <VBtn
          :disabled="ui.submitting"
          text
          @click="
            $store.commit('ui/setRegisterModal', { value: false }),
            $store.commit('ui/setBitrixLoginModal', { value: true })"
          v-text="'Через Битрикс24'"
        />
      </VCardTitle>

      <VCardText>
        <VForm>
          <VTextField
            ref="companyName"
            v-model="model.companyName"
            :error-messages="companyNameErrors"
            label="Название компании"
            autofocus
            @change="$v.model.companyName.$touch()"
            @blur="$v.model.companyName.$touch()"
            @keydown.enter="touchAndSubmit()"
          />
          <VTextField
            ref="email"
            v-model="model.email"
            :error-messages="emailErrors"
            type="email"
            label="E-mail"
            @change="$v.model.email.$touch()"
            @blur="$v.model.email.$touch()"
            @keydown.enter="touchAndSubmit()"
          />
          <VTextField
            ref="firstName"
            v-model="model.firstName"
            :error-messages="firstNameErrors"
            label="Имя"
            @change="$v.model.firstName.$touch()"
            @blur="$v.model.firstName.$touch()"
            @keydown.enter="touchAndSubmit()"
          />
          <VTextField
            ref="lastName"
            v-model="model.lastName"
            :error-messages="lastNameErrors"
            label="Фамилия"
            @change="$v.model.lastName.$touch()"
            @blur="$v.model.lastName.$touch()"
            @keydown.enter="touchAndSubmit()"
          />
          <VTextField
            ref="password"
            v-model="model.password"
            label="Пароль"
            :type="ui.showPassword ? 'text' : 'password'"
            :append-icon="ui.showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :error-messages="passwordErrors"
            @change="$v.model.password.$touch()"
            @blur="$v.model.password.$touch()"
            @keydown.enter="touchAndSubmit()"
            @click:append="ui.showPassword = !ui.showPassword"
          />
          <VTextField
            ref="passwordConfirmation"
            v-model="model.passwordConfirmation"
            label="Повторите пароль"
            :type="ui.showPasswordConfirmation ? 'text' : 'password'"
            :append-icon="ui.showPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'"
            :error-messages="passwordConfirmationErrors"
            @change="$v.model.passwordConfirmation.$touch()"
            @blur="$v.model.passwordConfirmation.$touch()"
            @keydown.enter="touchAndSubmit()"
            @click:append="ui.showPasswordConfirmation = !ui.showPasswordConfirmation"
          />
        </VForm>
      </VCardText>

      <VDivider />

      <VCardActions>
        <VBtn
          :disabled="ui.submitting"
          text
          @click="$store.commit('ui/setRegisterModal', { value: false })"
        >
          Отмена
        </VBtn>
        <VSpacer />
        <VBtn
          :disabled="ui.submitting || $v.$error"
          :loading="ui.submitting"
          color="primary"
          depressed
          @click="touchAndSubmit()"
        >
          Зарегистрироваться
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, minLength, email, sameAs } from 'vuelidate/lib/validators'

export default {
  name: 'RegisterModal',
  mixins: [ validationMixin ],
  data: () => ({
    model: {
      companyName: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      passwordConfirmation: '',
    },
    ui: {
      submitting: false,
      error: null,
      errorDescriptions: {
        required: 'Обязательное поле',
        minLength3: 'Хотя бы 3 символа',
        minLength8: 'Хотя бы 8 символов',
        email: 'Требуется ввести корректный E-mail',
        passwordMatch: 'Пароли не совпадают',
      },
      showPassword: false,
      showPasswordConfirmation: false,
    },
  }),
  validations: {
    model: {
      companyName: { required, minLength3: minLength(3) },
      email: { required, email },
      firstName: { required, minLength3: minLength(3) },
      lastName: { required, minLength3: minLength(3) },
      password: { required, minLength8: minLength(8) },
      passwordConfirmation: {
        required,
        passwordMatch: sameAs(function() { return this.model.password }),
      },
    },
  },
  computed: {
    emailErrors() { return this.getErrorMessages('email') },
    firstNameErrors() { return this.getErrorMessages('firstName') },
    lastNameErrors() { return this.getErrorMessages('lastName') },
    companyNameErrors() { return this.getErrorMessages('companyName') },
    passwordErrors() { return this.getErrorMessages('password') },
    passwordConfirmationErrors() {
      return this.getErrorMessages('passwordConfirmation')
    },
  },
  watch: {
    '$store.state.ui.registerModal': {
      handler(isOpen) {
        if (isOpen) {
          this.focus()
        } else {
          this.model.email = ''
          this.model.firstName = ''
          this.model.lastName = ''
          this.model.password = ''
          this.model.passwordConfirmation = ''
          this.model.companyName = null
          this.ui.error = null
        }
        this.$v.$reset()
        this.$nextTick(() => this.$v.$reset())
        setTimeout(() => this.$v.$reset(), 250)
      },
      immediate: true,
    },
  },
  methods: {
    touchAndSubmit() {
      const {
        model: { email, firstName, lastName, password, companyName },
        ui: { submitting },
      } = this
      this.$v.$touch()
      if (this.$v.$error) {
        return
      }
      if (submitting) { return }

      this.ui.showPassword = this.ui.showPasswordConfirmation = false
      this.ui.submitting = true
      this.ui.error = null
      this.$store.dispatch(
        'auth/register',
        { email, firstName, lastName, password, companyName },
      )
        .finally(() => { this.ui.submitting = false })
        .catch(e => {
          this.ui.error = `${e.message}: попробуйте еще раз`
          throw e
        })
        .then(() => {
          this.$store.dispatch('user/getCurrent')
          this.$store.commit('ui/setRegisterModal', { value: false })
          return this.$nextTick()
        })
        .then(() => this.$root.$emit('app:register', { email, password }))
    },
    focus() {
      this.$nextTick().then(() => {
        const refToFocus = this.$refs.companyName
        if (refToFocus) {
          refToFocus.focus()
          setTimeout(refToFocus.focus, 200)
        }
      })
    },
    getErrorMessages(fieldName) {
      const { ui: { errorDescriptions } } = this
      const field = this.$v.model[fieldName]
      if (!field.$dirty) { return [] }
      return Object.entries(field)
        .filter(([ prop, val ]) =>
          errorDescriptions.hasOwnProperty(prop) && !val)
        .map(([ prop, _ ]) => errorDescriptions[prop])
    },
  },
}
</script>
